<template>
  <BaseModal modal-content-class="modal_change_info">
    <div class="content">
      <form>
        <h1>{{ $_translations.value.modal_change_password_title }}</h1>

        <BasePasswordFormInput
          :model-value="formValues.currentPassword.state"
          :label="$_translations.value.modal_change_username_input_label_current_password"
          :error="formValues.currentPassword.validation.error"
          :error-message="$_translations.value[formValues.currentPassword.validation.message]"
          input-ref="changePasswordCurrentRef"
          input-id="currentPassword"
          input-name="currentPassword"
          @update:modelValue="formValues.currentPassword.setState"
        />

        <BasePasswordFormInput
          :model-value="formValues.newPassword.state"
          :label="$_translations.value.modal_change_username_input_label_new_password"
          :error="formValues.newPassword.validation.error"
          :error-message="$_translations.value[formValues.newPassword.validation.message]"
          :max-size="passwordMaxSize"
          input-ref="changePasswordNewRef"
          input-id="newPassword"
          input-name="newPassword"
          @update:modelValue="formValues.newPassword.setState"
        />

        <BasePasswordFormInput
          :model-value="formValues.confirmPassword.state"
          :label="$_translations.value.modal_change_username_input_label_confirm_password"
          :error="formValues.confirmPassword.validation.error"
          :error-message="$_translations.value[formValues.confirmPassword.validation.message]"
          :max-size="passwordMaxSize"
          input-ref="changePasswordConfirmRef"
          input-id="confirmPassword"
          input-name="confirmPassword"
          @update:modelValue="formValues.confirmPassword.setState"
        />

        <a href="#" class="forgot_pin" @click.prevent="$emit('redirect:forgotPassword')">
          {{ $_translations.value.modal_change_password_link_forgot_password }}
        </a>

        <BaseButton
          container-class="update_info"
          :disabled="submitting"
          @click:button.prevent="handleSubmit"
        >
          {{ $_translations.value.modal_change_password_button_proceed }}
        </BaseButton>
      </form>
    </div>

    <template #bottom>
      <p v-if="isSuccess" class="succ_upd">
        {{ $_translations.value.modal_change_password_success_message }}
      </p>

      <p v-else-if="serverErrorMessage" class="error_text">
        {{ $_translations.value[serverErrorMessage] }}
      </p>
    </template>
  </BaseModal>
</template>

<script setup>
import { ref } from 'vue';
import BaseModal from '@/components/modal/BaseModal.vue';
import BasePasswordFormInput from '@/components/formInput/BasePasswordFormInput.vue';
import BaseButton from '@/components/button/BaseButton.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useFormHook from '@/hooks/useForm.js';
import useUILockHook from '@/hooks/useUILock.js';
import { apiChangePassword } from '@/services/user.js';
import { apiResponseTypeEnum } from '@/constants/api.js';
import { passwordValidationConfiguration } from '@/constants/validation.js';

defineEmits(['redirect:forgotPassword']);

const appSettingsStore = useAppSettingsStore();

const { formValues, isFormInvalid, resetFormErrors } = useFormHook({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});
const [submitting, setSubmittingState] = useUILockHook();

const isSuccess = ref(false);
const serverErrorMessage = ref('');

const passwordMaxSize = appSettingsStore.appProperties.maxLengthPinDigits;

const validateForm = () => {
  if ([formValues.currentPassword.state, formValues.newPassword.state, formValues.confirmPassword.state].some(value => !value)) {
    if (!formValues.currentPassword.state) {
      formValues.currentPassword.setValidation({ error: true, message: 'common_error_input_field_required' });
    }
    if (!formValues.newPassword.state) {
      formValues.newPassword.setValidation({ error: true, message: 'common_error_input_field_required' });
    }
    if (!formValues.confirmPassword.state) {
      formValues.confirmPassword.setValidation({ error: true, message: 'common_error_input_field_required' });
    }
  } else if (formValues.newPassword.state.length < passwordValidationConfiguration.minSize) {
    formValues.newPassword.setValidation({ error: true, message: 'common_error_password_min_size_restriction' });
  } else if (formValues.newPassword.state.length > passwordMaxSize) {
    formValues.newPassword.setValidation({ error: true, message: 'common_error_password_max_size_restriction' });
  } else if (!formValues.newPassword.state.match(passwordValidationConfiguration.formatRegex)) {
    formValues.newPassword.setValidation({ error: true, message: 'common_error_password_format_restriction' });
  } else if (formValues.newPassword.state !== formValues.confirmPassword.state) {
    formValues.confirmPassword.setValidation({ error: true, message: 'common_error_passwords_do_not_match' });
  }

  return isFormInvalid();
};

const handleSubmit = async () => {
  try {
    setSubmittingState(true);

    isSuccess.value = false;
    serverErrorMessage.value = '';

    resetFormErrors();

    const invalid = validateForm();

    if (invalid) { return; }

    await apiChangePassword({
      payload: {
        passwordOld: formValues.currentPassword.state,
        password: formValues.confirmPassword.state,
      },
    });

    isSuccess.value = true;
  } catch (error) {
    if (error?.responseEnum === apiResponseTypeEnum.WRONG_PIN) {
      formValues.currentPassword.setValidation({ error: true, message: 'modal_change_password_error_current_password_invalid' });
    } else {
      serverErrorMessage.value = 'common_error_server_unavailable';
    }
  } finally {
    setSubmittingState(false);
  }
};
</script>
